/**
 * Custom settings menu item that automatically translates labels
 * using media-chrome's i18n system
 */

import { t } from 'media-chrome/utils/i18n.js';
import type { TranslateKeys } from '../lang/en.js';
import { MediaSettingsMenuItem } from 'media-chrome/menu/media-settings-menu-item.js';

class BunnySettingsMenuItem extends MediaSettingsMenuItem {
  static override get observedAttributes() {
    return [
      ...(MediaSettingsMenuItem.observedAttributes || []),
      'translate-key',
    ];
  }

  override connectedCallback() {
    super.connectedCallback();
    // Delay translation until after shadow DOM is fully initialized
    requestAnimationFrame(() => this.#updateTranslation());
  }

  override attributeChangedCallback(
    attrName: string,
    oldValue: string | null,
    newValue: string | null,
  ) {
    super.attributeChangedCallback?.(attrName, oldValue, newValue);

    if (attrName === 'translate-key' && oldValue !== newValue) {
      this.#updateTranslation();
    }
  }

  #updateTranslation() {
    const key = this.getAttribute('translate-key');
    if (!key) return;

    const translated = t(key as TranslateKeys);

    // Update aria-label
    this.setAttribute('aria-label', translated);

    // Update text content (direct child text node)
    const textNode = Array.from(this.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim(),
    );
    if (textNode) {
      textNode.textContent = translated;
    }

    // Update title slot (for submenu title)
    const titleSlot = this.querySelector('[slot="title"]');
    if (titleSlot) {
      titleSlot.textContent = translated;
    }
  }
}

if (!globalThis.customElements.get('bunny-settings-menu-item')) {
  globalThis.customElements.define(
    'bunny-settings-menu-item',
    BunnySettingsMenuItem,
  );
}

export default BunnySettingsMenuItem;
